import { Injectable } from '@angular/core';
import { LegFiJwtService } from '../auth/legfi-jwt.service';

/**
 * Single source of truth for "where does Requests live for THIS org".
 *
 * V2 and V3 are two parallel surfaces on different URLs — `/app/forms` +
 * `/app/form-submission/:id` versus `/app/requests` + `/app/requests/submission/:id`
 * — selected by the per-org `ui-v3-requests` flag. Before this service the flag was
 * consulted in exactly ONE place (app-navigation.service, for the sidenav target);
 * every other link in the Requests area hardcoded the V2 paths. A V3 org could open
 * the inbox from the nav, drill into a form, click a submission, and silently land on
 * the V2 detail page — and its back button then returned them to the V2 dashboard.
 *
 * Anything that navigates BETWEEN Requests pages must resolve its target through here.
 * Deep links from outside (emails, stored notifications) intentionally keep pointing at
 * the canonical V2 paths and are NOT rewritten — those routes stay live for everyone.
 */
@Injectable({ providedIn: 'root' })
export class RequestsRoutingService {
    /**
     * Read per call rather than cached at construction: the JWT is re-minted on org
     * switch, and this service is a root singleton that would otherwise hand a stale
     * answer to every page after the first.
     */
    get v3Enabled(): boolean {
        return LegFiJwtService.featureEnabled('ui-v3-requests');
    }

    /** The Requests landing page — V3 dashboard, or the V2 form library. */
    get root(): string {
        return this.v3Enabled ? '/app/requests' : '/app/forms';
    }

    /** A single submission's detail page. */
    submissionDetail(submissionId: number | string): string {
        return this.v3Enabled
            ? `/app/requests/submission/${submissionId}`
            : `/app/form-submission/${submissionId}`;
    }

    /** routerLink-friendly array form of {@link root}. */
    get rootLink(): string[] {
        return [this.root];
    }

    /** routerLink-friendly array form of {@link submissionDetail}. */
    submissionDetailLink(submissionId: number | string): string[] {
        return [this.submissionDetail(submissionId)];
    }
}
